import React, { useState, useEffect } from "react";
import { db, auth } from "../firebaseConfig";
import { collection, getDocs, getDoc, doc, setDoc, updateDoc } from "firebase/firestore";
import {
  Button,
  Card,
  Col,
  Row,
  Form,
  Table,
  Modal,
  Alert,
} from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";

export default function UserManagement() {
  const [role, setRole] = useState("");
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [newRoleName, setNewRoleName] = useState("");
  const [rolePermissions, setRolePermissions] = useState({
    canAccessPayrolls: false,
    canManagePayrolls: false,
    canClockInOut: false,
    canViewClockInOut: false,
    canManageLeaves: false,
    canApproveDenyLeaves: false,
    canUploadDocuments: false,
    canViewYearlyDocuments: false,
    canViewInvoice: false,
    canMakeAnnouncements: false,
    canCreateInvoices: false,
    canSendSignRequests: false, // New permission added
  });
  const [customRoles, setCustomRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const fetchUserRole = async () => {
    const user = auth.currentUser;
    if (user) {
      const userDocRef = doc(db, "Users", user.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        setRole(userDoc.data().role || "");
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, "Users"));
      const userList = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(userList);
      setFilteredUsers(userList);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchCustomRoles = async () => {
    try {
      const rolesSnapshot = await getDocs(collection(db, "Roles"));
      const rolesList = rolesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCustomRoles(rolesList);
    } catch (err) {
      console.error("Error fetching roles:", err);
    }
  };

  useEffect(() => {
    fetchUserRole();
    fetchUsers();
    fetchCustomRoles();
  }, []);

  const handleSearch = (query) => {
    setSearchQuery(query);
    const lowerCaseQuery = query.toLowerCase();
    const filtered = users.filter(
      (user) =>
        user.fullName?.toLowerCase().includes(lowerCaseQuery) ||
        user.email?.toLowerCase().includes(lowerCaseQuery)
    );
    setFilteredUsers(filtered);
  };

  const handleCreateRole = async () => {
    if (!newRoleName) {
      alert("Role name cannot be empty.");
      return;
    }
    try {
      const newRole = {
        name: newRoleName,
        permissions: rolePermissions,
      };
      await setDoc(doc(db, "Roles", newRoleName), newRole);
      alert("Role created successfully!");
      fetchCustomRoles();
      setNewRoleName("");
      setRolePermissions({
        canAccessPayrolls: false,
        canManagePayrolls: false,
        canClockInOut: false,
        canViewClockInOut: false,
        canManageLeaves: false,
        canApproveDenyLeaves: false,
        canUploadDocuments: false,
        canViewYearlyDocuments: false,
        canViewInvoice: false,
        canMakeAnnouncements: false,
        canCreateInvoices: false,
        canSendSignRequests: false, // Reset new permission
      });
    } catch (err) {
      console.error("Error creating role:", err);
      alert("Failed to create role.");
    }
  };

  const handleAssignRole = async () => {
    if (!selectedUser || !selectedUser.id) {
      alert("Please select a user.");
      return;
    }

    if (selectedUser.newRole === "admin" && role !== "admin") {
      alert("Only admin users can assign the 'admin' role.");
      return;
    }

    try {
      await updateDoc(doc(db, "Users", selectedUser.id), {
        role: selectedUser.newRole,
      });
      alert("Role assigned successfully!");
      fetchUsers();
      setShowModal(false);
    } catch (err) {
      console.error("Error assigning role:", err);
      alert("Failed to assign role.");
    }
  };

  const handleEditRole = (role) => {
    setEditRole(role);
    setShowEditModal(true);
  };

  const handleUpdateRolePermissions = async () => {
    if (!editRole) return;

    const updatedPermissions = {};
    Object.keys(rolePermissions).forEach((key) => {
      updatedPermissions[key] = !!editRole.permissions[key];
    });

    try {
      await updateDoc(doc(db, "Roles", editRole.name), { permissions: updatedPermissions });
      alert("Role updated successfully!");
      fetchCustomRoles();
      setShowEditModal(false);
    } catch (err) {
      console.error("Error updating role permissions:", err);
      alert("Failed to update role.");
    }
  };

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {role !== "admin" ? (
          <div className="bg-gradient-danger text-white rounded-4 p-4 shadow-lg">
            <div className="d-flex align-items-center">
              <i className="ri-error-warning-line fs-4 me-3"></i>
              <div>
                <h6 className="mb-0 fw-bold">Access Denied</h6>
                <p className="mb-0">This page is accessible by admin users only.</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Enhanced Header Section */}
            <div className="text-center mb-5">
              <div className="d-flex align-items-center justify-content-center mb-3">
                <i className="ri-shield-user-line fs-1 text-primary me-3"></i>
                <h1 className="display-6 fw-bold text-primary mb-0">User Management</h1>
              </div>
              <p className="lead text-muted">Manage user roles and permissions for your organization</p>
            </div>

            {/* Enhanced User List Section */}
            <Card className="border-0 shadow-lg rounded-4 mb-5">
              <div className="bg-gradient-primary text-white p-4 rounded-top-4" 
                   style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <i className="ri-user-line fs-4 me-3"></i>
                    <h5 className="mb-0 fw-bold">User List</h5>
                  </div>
                  <span className="badge bg-white text-primary px-3 py-2 rounded-pill">
                    {filteredUsers.length} users
                  </span>
                </div>
              </div>
              <Card.Body className="p-4">
                <Form.Control
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="form-control-lg shadow-sm border-2 mb-4"
                  style={{ borderRadius: '12px' }}
                />
                <div className="table-responsive">
                  <Table className="table-hover table-borderless">
                    <thead className="bg-primary text-white">
                      <tr>
                        <th className="border-0">
                          <i className="ri-user-line me-2"></i>Name
                        </th>
                        <th className="border-0">
                          <i className="ri-mail-line me-2"></i>Email
                        </th>
                        <th className="border-0">
                          <i className="ri-shield-user-line me-2"></i>Role
                        </th>
                        <th className="border-0">
                          <i className="ri-settings-3-line me-2"></i>Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.id}
                            style={{ 
                              cursor: "pointer",
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                              e.currentTarget.style.transform = 'scale(1.01)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}>
                          <td className="fw-semibold">{user.fullName}</td>
                          <td className="fw-semibold">{user.email}</td>
                          <td>
                            <span className={`badge rounded-pill px-3 py-2 fw-semibold ${
                              user.role === "admin" 
                                ? "bg-danger" 
                                : user.role === "deleted"
                                ? "bg-secondary"
                                : user.role
                                ? "bg-success"
                                : "bg-warning text-dark"
                            }`}>
                              {user.role || "No role assigned"}
                            </span>
                          </td>
                          <td>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => {
                                setSelectedUser({ ...user, newRole: "" });
                                setShowModal(true);
                              }}
                              className="rounded-pill"
                            >
                              <i className="ri-user-settings-line me-1"></i>Assign Role
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>

            {/* Enhanced Create Role Section */}
            <Card className="border-0 shadow-lg rounded-4 mb-5">
              <div className="bg-gradient-success text-white p-4 rounded-top-4" 
                   style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-add-line fs-4 me-3"></i>
                  <h5 className="mb-0 fw-bold">Create a New Role</h5>
                </div>
              </div>
              <Card.Body className="p-4">
                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold">
                    <i className="ri-shield-user-line me-2"></i>Role Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter role name"
                    value={newRoleName}
                    onChange={(e) => setNewRoleName(e.target.value)}
                    className="form-control-lg shadow-sm border-2"
                    style={{ borderRadius: '12px' }}
                  />
                </Form.Group>
                <h6 className="fw-bold text-primary mb-3">
                  <i className="ri-settings-3-line me-2"></i>Permissions
                </h6>
                <Row className="g-3">
                  {Object.keys(rolePermissions).map((key) => (
                    <Col md="6" key={key}>
                      <div className="bg-light rounded-3 p-3">
                        <Form.Check
                          type="checkbox"
                          label={key.replace(/([A-Z])/g, " $1").trim()}
                          checked={rolePermissions[key]}
                          onChange={(e) =>
                            setRolePermissions({
                              ...rolePermissions,
                              [key]: e.target.checked,
                            })
                          }
                          className="fw-semibold"
                        />
                      </div>
                    </Col>
                  ))}
                </Row>
                <div className="text-center mt-4">
                  <Button 
                    variant="success" 
                    size="lg"
                    onClick={handleCreateRole}
                    className="px-5 py-3 rounded-pill fw-semibold shadow-lg"
                    style={{ 
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <i className="ri-add-line me-2"></i>Create Role
                  </Button>
                </div>
              </Card.Body>
            </Card>

            {/* Enhanced Edit Roles Section */}
            <Card className="border-0 shadow-lg rounded-4 mb-5">
              <div className="bg-gradient-warning text-white p-4 rounded-top-4" 
                   style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <i className="ri-edit-line fs-4 me-3"></i>
                    <h5 className="mb-0 fw-bold">Edit Existing Roles</h5>
                  </div>
                  <span className="badge bg-white text-warning px-3 py-2 rounded-pill">
                    {customRoles.length} roles
                  </span>
                </div>
              </div>
              <Card.Body className="p-4">
                <div className="table-responsive">
                  <Table className="table-hover table-borderless">
                    <thead className="bg-primary text-white">
                      <tr>
                        <th className="border-0">
                          <i className="ri-shield-user-line me-2"></i>Role Name
                        </th>
                        <th className="border-0">
                          <i className="ri-settings-3-line me-2"></i>Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {customRoles.map((role) => (
                        <tr key={role.id}
                            style={{ 
                              cursor: "pointer",
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                              e.currentTarget.style.transform = 'scale(1.01)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}>
                          <td className="fw-semibold">{role.name}</td>
                          <td>
                            <Button
                              variant="warning"
                              size="sm"
                              onClick={() => handleEditRole(role)}
                              className="rounded-pill"
                            >
                              <i className="ri-edit-line me-1"></i>Edit Permissions
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>

            {/* Enhanced Assign Role Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
              <Modal.Header className="bg-gradient-primary text-white border-0">
                <Modal.Title className="fw-bold">
                  <i className="ri-user-settings-line me-2"></i>
                  Assign Role
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="p-4">
                <div className="bg-primary bg-opacity-10 rounded-3 p-4 text-center mb-4">
                  <h6 className="text-primary fw-bold mb-2">
                    Assign a role to <strong>{selectedUser?.fullName}</strong>
                  </h6>
                  <p className="text-muted mb-0">{selectedUser?.email}</p>
                </div>
                <Form.Select
                  value={selectedUser?.newRole || ""}
                  onChange={(e) =>
                    setSelectedUser({ ...selectedUser, newRole: e.target.value })
                  }
                  className="form-control-lg shadow-sm border-2"
                  style={{ borderRadius: '12px' }}
                >
                  <option value="">Select a role</option>
                  <option value="admin">Admin</option>
                  {customRoles.map((role) => (
                    <option key={role.id} value={role.name}>
                      {role.name}
                    </option>
                  ))}
                </Form.Select>
              </Modal.Body>
              <Modal.Footer className="border-0 p-4">
                <Button variant="secondary" onClick={() => setShowModal(false)} className="px-4 py-2 rounded-pill fw-semibold">
                  <i className="ri-close-line me-2"></i>Cancel
                </Button>
                <Button variant="primary" onClick={handleAssignRole} className="px-4 py-2 rounded-pill fw-semibold">
                  <i className="ri-check-line me-2"></i>Assign Role
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Enhanced Edit Role Permissions Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
              <Modal.Header className="bg-gradient-warning text-white border-0">
                <Modal.Title className="fw-bold">
                  <i className="ri-edit-line me-2"></i>
                  Edit Role Permissions
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="p-4">
                {editRole && (
                  <>
                    <div className="bg-warning bg-opacity-10 rounded-3 p-4 text-center mb-4">
                      <h5 className="text-warning fw-bold mb-0">Role: {editRole.name}</h5>
                    </div>
                    <h6 className="fw-bold text-primary mb-3">
                      <i className="ri-settings-3-line me-2"></i>Permissions
                    </h6>
                    <Row className="g-3">
                      {Object.keys(rolePermissions).map((key) => (
                        <Col md="6" key={key}>
                          <div className="bg-light rounded-3 p-3">
                            <Form.Check
                              type="checkbox"
                              label={key.replace(/([A-Z])/g, " $1").trim()}
                              checked={editRole.permissions[key] || false}
                              onChange={(e) =>
                                setEditRole({
                                  ...editRole,
                                  permissions: {
                                    ...editRole.permissions,
                                    [key]: e.target.checked,
                                  },
                                })
                              }
                              className="fw-semibold"
                            />
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </>
                )}
              </Modal.Body>
              <Modal.Footer className="border-0 p-4">
                <Button variant="secondary" onClick={() => setShowEditModal(false)} className="px-4 py-2 rounded-pill fw-semibold">
                  <i className="ri-close-line me-2"></i>Cancel
                </Button>
                <Button variant="primary" onClick={handleUpdateRolePermissions} className="px-4 py-2 rounded-pill fw-semibold">
                  <i className="ri-save-line me-2"></i>Update Permissions
                </Button>
              </Modal.Footer>
            </Modal>
          </>
        )}
        <Footer />
      </div>
    </React.Fragment>
  );
}
